// src/components/sections/upload/ItineraryUploadSection.jsx
import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Users, Globe, Star } from 'lucide-react';

const ItineraryUploadSection = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');

  // Your Google Apps Script Web App URL
  const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzeFNuDv35VxcvtFesYocGpxagZiUA5Y54H92QppNBfXJAhJFQA1dM9aHCukIb4iU8B/exec';

  const acceptedFileTypes = ['.pdf', '.doc', '.docx', '.txt'];
  const maxFileSize = 50 * 1024 * 1024; // 50MB

  const features = [
    {
      icon: <Users className="w-6 h-6 text-[#FF6B35]" />,
      title: "Help Fellow Travelers",
      description: "Share your amazing itineraries with our community"
    },
    {
      icon: <Globe className="w-6 h-6 text-[#FF6B35]" />,
      title: "Global Impact",
      description: "Your experiences can inspire travelers worldwide"
    },
    {
      icon: <Star className="w-6 h-6 text-[#FF6B35]" />,
      title: "Get Featured",
      description: "Quality itineraries may be featured on our platform"
    }
  ];

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const isValidType = acceptedFileTypes.some(type => 
        file.name.toLowerCase().endsWith(type.toLowerCase())
      );
      const isValidSize = file.size <= maxFileSize;
      
      if (!isValidType) {
        alert(`File "${file.name}" is not a supported format. Please use PDF, DOC, DOCX, or TXT files.`);
        return false;
      }
      
      if (!isValidSize) {
        alert(`File "${file.name}" is too large (${formatFileSize(file.size)}). Maximum size is 50MB. Please email larger files to travelsquad707@gmail.com`);
        return false;
      }
      
      return true;
    });

    const newFiles = validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      status: 'ready'
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:mime;base64, prefix
        resolve({
          name: file.name,
          content: base64,
          mimeType: file.type,
          size: file.size
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadFilesToDrive = async (files, name, email, message, submissionId) => {
    try {
      console.log('Starting upload process...');
      console.log('Files to upload:', files.length);
      console.log('Apps Script URL:', APPS_SCRIPT_URL);
      console.log('Submission ID:', submissionId);

      // Convert all files to base64
      const base64Files = await Promise.all(
        files.map(fileObj => convertFileToBase64(fileObj.file))
      );

      console.log('Files converted to base64:', base64Files.length);

      // Create URL-encoded form data (not FormData)
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('message', message);
      formData.append('submissionId', submissionId);
      formData.append('filesData', JSON.stringify(base64Files));

      console.log('Sending request to Apps Script...');

      const response = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      const result = JSON.parse(responseText);
      console.log('Parsed result:', result);
      
      if (result.success) {
        return {
          success: true,
          files: result.files,
          message: result.message,
          summaryFile: result.summaryFile
        };
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('File upload error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadedFiles.length === 0 || !email || !name) return;

    setIsUploading(true);
    
    try {
      // Update file statuses to uploading
      setUploadedFiles(prev => prev.map(f => ({ ...f, status: 'uploading' })));
      
      const submissionId = `SUB_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Step 1: Upload files to Google Drive via Apps Script
      const uploadResult = await uploadFilesToDrive(uploadedFiles, name, email, message, submissionId);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }

      // Files uploaded successfully!
      console.log('Upload completed successfully:', uploadResult);

      // Update file statuses to success
      setUploadedFiles(prev => prev.map(f => ({ ...f, status: 'success' })));
      setSubmitStatus('success');

      // Reset form after delay
      setTimeout(() => {
        setUploadedFiles([]);
        setName('');
        setEmail('');
        setMessage('');
        setSubmitStatus('');
      }, 5000);
      
    } catch (error) {
      console.error('Submission failed:', error);
      setUploadedFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
      setSubmitStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section id="upload" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B35] to-[#FF8C35]">
              Share Your Travel Itineraries
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Help us build the world's largest collection of authentic travel experiences. 
              Upload your itineraries and inspire fellow travelers!
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-left p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <form onSubmit={handleSubmit}>
              {/* File Upload Area */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-[#FF6B35] bg-orange-50' 
                    : 'border-gray-300 hover:border-[#FF6B35]'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Drop your itinerary files here
                </h3>
                <p className="text-gray-500 mb-4">
                  Or click to browse your files
                </p>
                <p className="text-sm text-gray-400">
                  Accepted formats: PDF, DOC, DOCX, TXT (Max 50MB per file)
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Larger files may take longer to upload. Files over 50MB? Email them to travelsquad707@gmail.com
                </p>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3">Uploaded Files:</h4>
                  <div className="space-y-2">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-gray-500" />
                          <div>
                            <p className="font-medium text-gray-700">{file.name}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.status === 'ready' && (
                            <button
                              type="button"
                              onClick={() => removeFile(file.id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {file.status === 'uploading' && (
                            <div className="w-5 h-5 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
                          )}
                          {file.status === 'success' && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                          {file.status === 'error' && (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-none"
                    placeholder="Tell us about your itinerary..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-6 text-center">
                <button
                  type="submit"
                  disabled={uploadedFiles.length === 0 || !email || !name || isUploading}
                  className={`inline-flex items-center px-8 py-3 rounded-full transition-all transform hover:scale-105 shadow-lg hover:shadow-xl ${
                    uploadedFiles.length === 0 || !email || !name || isUploading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#FF6B35] to-[#FF8C35] text-white hover:from-[#FF8C35] hover:to-[#FF6B35] hover:text-white'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading to Drive...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Submit Itinerary
                    </>
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 rounded-lg text-center bg-green-50 text-green-700 border border-green-200">
                  ✅ Success! Your itinerary files and information have been uploaded to our review system. Thank you for sharing your travel experiences!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 rounded-lg text-center bg-red-50 text-red-700 border border-red-200">
                  ❌ Upload failed. Please check your internet connection and try again.
                </div>
              )}
            </form>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              📝 By uploading, you agree to let us review and potentially feature your itinerary. 
              We'll credit you as the creator and reach out if we'd like to include it in our collection.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ItineraryUploadSection;